import React from 'react';
import { 
  Paper, 
  Typography, 
  Box, 
  LinearProgress,
  Chip,
} from '@mui/material';
import { 
  BarChart as BarChartIcon,
  CompareArrows as CompareArrowsIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { Chart } from 'react-google-charts';

// Helper function to determine data source and get trust scores
const getTrustScoresFromData = (userData) => {
  let trustScores = [];
  let dataSource = 'none';
  
  // First try to get data from myCurrentNetworkList
  if (userData && userData.myCurrentNetworkList && userData.myCurrentNetworkList.userDetails && userData.myCurrentNetworkList.userDetails.length > 0) {
    trustScores = userData.myCurrentNetworkList.userDetails.map(user => user.trustScore).filter(score => score !== undefined && score !== null);
    dataSource = 'myCurrentNetworkList';
  }
  // If myCurrentNetworkList is empty, try firstDegreeFriends
  else if (userData && userData.firstDegreeFriends && userData.firstDegreeFriends.length > 0) {
    trustScores = userData.firstDegreeFriends.map(friend => friend.trustRating).filter(score => score !== undefined && score !== null);
    dataSource = 'firstDegreeFriends';
  }
  
  return { trustScores, dataSource };
};

// Trust Score Distribution Component
const TrustScoreDistribution = ({ userData }) => {
  const calculateTrustDistribution = () => {
    const { trustScores, dataSource } = getTrustScoresFromData(userData);
    
    // If we have actual trust scores, calculate real distribution
    if (trustScores.length > 0) {
      const distribution = {
        '50-59': 0,
        '60-69': 0,
        '70-79': 0,
        '80-89': 0,
        '90-100': 0
      };

      trustScores.forEach(trustScore => {
        if (trustScore >= 90) {
          distribution['90-100']++;
        } else if (trustScore >= 80) {
          distribution['80-89']++;
        } else if (trustScore >= 70) {
          distribution['70-79']++;
        } else if (trustScore >= 60) {
          distribution['60-69']++;
        } else if (trustScore >= 50) {
          distribution['50-59']++;
        }
      });

      const chartData = [['Trust Score Range', 'Number of Connections']];
      Object.entries(distribution).forEach(([range, count]) => {
        chartData.push([range, count]);
      });

      return { chartData, dataSource, hasData: true };
    }
    
    // If no data available, return engaging demo data
    const demoData = [
      ['Trust Score Range', 'Number of Connections'],
      ['50-59', 3],
      ['60-69', 7],
      ['70-79', 12],
      ['80-89', 15],
      ['90-100', 8]
    ];
    
    return { chartData: demoData, dataSource: 'demo', hasData: false };
  };

  const { chartData, dataSource, hasData } = calculateTrustDistribution();
  
  // Find the most common trust score range
  const dataRows = chartData.slice(1);
  const mostCommonRange = dataRows.reduce(
    (prev, current) => (current[1] > prev[1] ? current : prev),
    ['', 0]
  );

  const getInsightMessage = () => {
    if (!hasData) {
      return "This is how your network's trust score distribution could look! Start connecting with trusted colleagues, clients, and business partners to build a strong, reliable network.";
    }
    
    if (dataSource === 'firstDegreeFriends') {
      return `Based on your ${getTrustScoresFromData(userData).trustScores.length} connection${getTrustScoresFromData(userData).trustScores.length !== 1 ? 's' : ''}, most have trust scores in the ${mostCommonRange[0]} range, indicating a ${mostCommonRange[0] === '80-89' || mostCommonRange[0] === '90-100' ? 'reliable' : 'developing'} network.`;
    }
    
    return mostCommonRange[1] > 0 
      ? `Most of your network connections have trust scores in the range of ${mostCommonRange[0]}, indicating a ${mostCommonRange[0] === '80-89' || mostCommonRange[0] === '90-100' ? 'reliable' : 'developing'} network.`
      : 'Start building your network to see trust score insights.';
  };

  // Custom chart component that works reliably
  const CustomBarChart = ({ isDemo = false }) => (
    <Box sx={{ 
      height: 200, 
      p: 2, 
      bgcolor: isDemo ? '#f8f9fa' : 'white', 
      borderRadius: 1, 
      display: 'flex', 
      flexDirection: 'column', 
      justifyContent: 'center',
      border: isDemo ? '2px dashed #81c784' : '1px solid #e0e0e0'
    }}>
      <Typography variant="h6" align="center" color={isDemo ? 'success.main' : 'primary.main'} gutterBottom>
        {isDemo ? 'Trust Score Preview' : ''}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'end', height: 120, px: 1 }}>
        {dataRows.map(([range, count], index) => {
          const maxCount = Math.max(...dataRows.map(d => d[1]));
          const height = maxCount > 0 ? (count / maxCount) * 80 : 10;
          return (
            <Box key={range} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, mx: 0.5 }}>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 32,
                  height: `${height}px`,
                  bgcolor: isDemo ? 'success.main' : 'primary.main',
                  borderRadius: '3px 3px 0 0',
                  mb: 1,
                  opacity: isDemo ? 0.8 : 1,
                  transition: 'all 0.3s ease'
                }}
              />
              <Typography variant="caption" sx={{ fontSize: '10px', textAlign: 'center', lineHeight: 1.1 }}>
                {range}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '9px', fontWeight: 'bold' }}>
                {count}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
        Trust Score Range → Number of Connections
      </Typography>
    </Box>
  );

  return (
    <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <BarChartIcon sx={{ color: 'primary.main', mr: 1 }} />
        <Typography variant="h6">Trust Score Distribution</Typography>
        {!hasData && (
          <Chip 
            label="Preview" 
            size="small" 
            color="primary" 
            variant="outlined" 
            sx={{ ml: 1 }}
          />
        )}
      </Box>
      
      {/* Always use custom chart to avoid Google Charts errors */}
      <CustomBarChart isDemo={!hasData} />
      
      <Box sx={{ 
        mt: 2, 
        p: 2, 
        bgcolor: hasData ? 'grey.50' : 'success.light', 
        color: hasData ? 'text.secondary' : 'white',
        borderRadius: 1 
      }}>
        <Typography variant="body2">
          {getInsightMessage()}
        </Typography>
        {!hasData && (
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
            Connect with 5+ people to unlock real insights about your network!
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

// Network Comparison Component
const NetworkComparison = ({ userData }) => {
  const calculateNetworkStats = () => {
    const platformAvg = 72;
    const { trustScores, dataSource } = getTrustScoresFromData(userData);
    
    let yourNetworkAvg = 0;
    let percentile = 0;
    let hasData = false;
    
    if (trustScores.length > 0) {
      hasData = true;
      const totalScore = trustScores.reduce((sum, score) => sum + score, 0);
      yourNetworkAvg = Math.round(totalScore / trustScores.length);
      
      // Calculate percentile
      if (yourNetworkAvg > platformAvg) {
        percentile = 50 + Math.round((yourNetworkAvg - platformAvg) / (100 - platformAvg) * 49);
      } else {
        percentile = Math.round((yourNetworkAvg / platformAvg) * 50);
      }
    } else {
      // Demo data for empty state
      yourNetworkAvg = 78;
      percentile = 65;
      hasData = false;
    }
    
    return {
      yourNetworkAvg,
      platformAvg,
      percentile,
      hasData,
      dataSource,
      connectionCount: trustScores.length
    };
  };

  const networkStats = calculateNetworkStats();

  return (
    <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <CompareArrowsIcon sx={{ color: 'primary.main', mr: 1 }} />
        <Typography variant="h6">Network Comparison</Typography>
        {!networkStats.hasData && (
          <Chip 
            label="Preview" 
            size="small" 
            color="primary" 
            variant="outlined" 
            sx={{ ml: 1 }}
          />
        )}
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">Your Network Average</Typography>
          <Typography variant="body2" fontWeight="bold">
            {networkStats.yourNetworkAvg}
            {!networkStats.hasData && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (potential)
              </Typography>
            )}
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={networkStats.yourNetworkAvg} 
          sx={{ 
            height: 10, 
            borderRadius: 5, 
            mb: 2,
            bgcolor: networkStats.hasData ? 'grey.300' : 'primary.100'
          }} 
          color={networkStats.hasData ? 'primary' : 'success'}
        />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">Platform Average</Typography>
          <Typography variant="body2" fontWeight="bold">{networkStats.platformAvg}</Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={networkStats.platformAvg} 
          sx={{ height: 10, borderRadius: 5, bgcolor: 'grey.300' }} 
          color="secondary"
        />
      </Box>
      
      {networkStats.hasData ? (
        <Box sx={{ 
          textAlign: 'center', 
          p: 2, 
          bgcolor: networkStats.percentile > 75 ? 'success.light' : networkStats.percentile > 50 ? 'primary.light' : networkStats.percentile > 25 ? 'warning.light' : 'error.light', 
          color: 'white', 
          borderRadius: 1 
        }}>
          <Typography variant="h5">Top {networkStats.percentile}%</Typography>
          <Typography variant="body2">
            {networkStats.percentile > 75 ? 'Your network trust quality is exceptional!' : 
             networkStats.percentile > 50 ? 'Your network trust quality is above average' : 
             networkStats.percentile > 25 ? 'Your network trust quality needs some improvement' : 
             'Your network trust quality needs significant improvement'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>
            Based on {networkStats.connectionCount} connection{networkStats.connectionCount !== 1 ? 's' : ''}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ 
          textAlign: 'center', 
          p: 2, 
          bgcolor: 'success.light', 
          color: 'white', 
          borderRadius: 1 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <TrendingUpIcon sx={{ mr: 1 }} />
            <Typography variant="h5">Your Potential: Top 65%</Typography>
          </Box>
          <Typography variant="body2">
            This is what you could achieve! Start building quality connections to unlock real network insights.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
            Quality connections lead to better opportunities 🚀
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

// Connection Longevity Component
const ConnectionLongevity = ({ userData }) => {
  const calculateLongevityDistribution = () => {
    let connectionsData = [];
    let hasRealData = false;
    
    // Try to get data from firstDegreeFriends first (more likely to have longevity data)
    if (userData && userData.firstDegreeFriends && userData.firstDegreeFriends.length > 0) {
      connectionsData = userData.firstDegreeFriends;
      hasRealData = true;
    }
    // Fallback to myCurrentNetworkList if available
    else if (userData && userData.myCurrentNetworkList && userData.myCurrentNetworkList.userDetails && userData.myCurrentNetworkList.userDetails.length > 0) {
      connectionsData = userData.myCurrentNetworkList.userDetails;
      hasRealData = true;
    }
    
    // Initialize distribution
    const distribution = {
      'less1month': 0,
      '1to3months': 0,
      '3to6months': 0,
      '6to12months': 0,
      'more12months': 0
    };
    
    if (hasRealData) {
      // Calculate actual distribution
      const currentDate = new Date();
      
      connectionsData.forEach(connection => {
        if (!connection.creationTime) return;
        
        const connectionDate = new Date(connection.creationTime);
        const diffTime = currentDate - connectionDate;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        const diffMonths = diffDays / 30;
        
        if (diffMonths < 1) {
          distribution.less1month++;
        } else if (diffMonths < 3) {
          distribution['1to3months']++;
        } else if (diffMonths < 6) {
          distribution['3to6months']++;
        } else if (diffMonths < 12) {
          distribution['6to12months']++;
        } else {
          distribution.more12months++;
        }
      });
    } else {
      // Use demo data
      distribution.less1month = 5;
      distribution['1to3months'] = 8;
      distribution['3to6months'] = 12;
      distribution['6to12months'] = 7;
      distribution.more12months = 3;
    }
    
    // Convert to Google Charts format
    const chartData = [
      ['Time Period', 'Connections'],
      ['< 1 month', distribution.less1month],
      ['1-3 months', distribution['1to3months']],
      ['3-6 months', distribution['3to6months']],
      ['6-12 months', distribution['6to12months']],
      ['> 12 months', distribution.more12months]
    ];
    
    return { chartData, hasData: hasRealData };
  };
  
  const { chartData, hasData } = calculateLongevityDistribution();
  
  const dataRows = chartData.slice(1);
  const mostCommonPeriod = dataRows.reduce(
    (prev, current) => (current[1] > prev[1] ? current : prev),
    ['', 0]
  );
  
  const totalConnections = dataRows.reduce((sum, row) => sum + row[1], 0);
  
  const generateInsight = () => {
    if (!hasData) {
      return "This shows how your network could develop over time! Building relationships consistently leads to a balanced mix of new and established connections.";
    }
    
    const recentPercentage = ((chartData[1][1] + chartData[2][1]) / totalConnections) * 100;
    const olderPercentage = ((chartData[4][1] + chartData[5][1]) / totalConnections) * 100;
    
    if (recentPercentage > 70) {
      return "Your network is very new. Focus on strengthening these recent connections to build lasting relationships.";
    } else if (olderPercentage > 70) {
      return "You have a mature network with many long-term connections. Consider expanding with some new connections to bring fresh perspectives.";
    } else if (mostCommonPeriod[0] === '3-6 months' || mostCommonPeriod[0] === '6-12 months') {
      return "Your network has a healthy balance of developing relationships. These middle-stage connections are ideal for deeper engagement.";
    } else {
      return `Most of your connections have been established for ${mostCommonPeriod[0]}, indicating a ${mostCommonPeriod[0] === '> 12 months' ? 'mature' : 'developing'} network.`;
    }
  };

  return (
    <Paper variant='outlined' sx={{ p: 3, mb: 2, border: 'none' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <TimelineIcon sx={{ color: 'primary.main', mr: 1 }} />
        <Typography variant="h6">Connection Longevity</Typography>
        {!hasData && (
          <Chip 
            label="Preview" 
            size="small" 
            color="primary" 
            variant="outlined" 
            sx={{ ml: 1 }}
          />
        )}
      </Box>
      
      {/* Google Charts Area Chart */}
      <Chart
        chartType="AreaChart"
        data={chartData}
        options={{
          legend: { position: 'none' },
          colors: [hasData ? '#1976d2' : '#81c784'],
          hAxis: { 
            title: 'Time Connected',
            titleTextStyle: { fontSize: 12 }
          },
          vAxis: { 
            title: 'Connections', 
            minValue: 0,
            titleTextStyle: { fontSize: 12 }
          },
          areaOpacity: hasData ? 0.3 : 0.5,
          backgroundColor: hasData ? 'white' : '#f8f9fa',
          chartArea: { width: '80%', height: '75%' },
          lineWidth: 2,
          pointSize: 4
        }}
        width="100%"
        height="200px"
      />
      
      <Box sx={{ 
        mt: 2, 
        p: 2, 
        bgcolor: hasData ? 'grey.50' : 'success.light', 
        color: hasData ? 'text.secondary' : 'white',
        borderRadius: 1 
      }}>
        <Typography variant="body2">
          {generateInsight()}
        </Typography>
        {!hasData && (
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
            ⏰ Consistent networking over time builds the strongest relationships!
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

// Main Network Insights Column Component
const NetworkInsightsColumn = ({ userData }) => {
  return (
    <>
      <TrustScoreDistribution userData={userData} />
      <NetworkComparison userData={userData}/>
      <ConnectionLongevity userData={userData} />
    </>
  );
};

export default NetworkInsightsColumn;